import {
  SCHOOL_EXERCISE_FAMILIES,
  SchoolExerciseFamilyRecord
} from '../curriculum/school-exercise-catalog';

export type ExerciseKind = string;
export type ExerciseQuestionKind = 'quantity' | 'compare' | 'sequence' | 'instruction';
export interface ExerciseDefinition extends SchoolExerciseFamilyRecord {}

interface BaseQuestion {
  id: string;
  kind: ExerciseQuestionKind;
  familyId: string;
  skillId: string;
  instruction: string;
  retryInstruction: string;
  expectedAnswer: number;
}

export interface QuantityExerciseQuestion extends BaseQuestion {
  kind: 'quantity';
  symbol: string;
  count: number;
  choices: readonly number[];
}

export interface CompareExerciseQuestion extends BaseQuestion {
  kind: 'compare';
  groups: readonly [
    { symbol: string; count: number; value: 0 },
    { symbol: string; count: number; value: 1 }
  ];
}

export interface SequenceExerciseQuestion extends BaseQuestion {
  kind: 'sequence';
  pattern: readonly string[];
  choices: readonly { value: number; visual: string; label: string }[];
}

export interface InstructionExerciseQuestion extends BaseQuestion {
  kind: 'instruction';
  choices: readonly { value: number; visual: string; label: string }[];
}

export type ExerciseQuestion =
  | QuantityExerciseQuestion
  | CompareExerciseQuestion
  | SequenceExerciseQuestion
  | InstructionExerciseQuestion;

export type RandomSource = () => number;
export const EXERCISES: readonly ExerciseDefinition[] = SCHOOL_EXERCISE_FAMILIES;

const SYMBOLS = [
  { visual: '🍎', label: 'mela' },
  { visual: '⭐', label: 'stella' },
  { visual: '🐟', label: 'pesce' },
  { visual: '🌼', label: 'fiore' },
  { visual: '🐞', label: 'coccinella' },
  { visual: '🚗', label: 'macchina' }
] as const;

const PATTERN_SYMBOLS = [
  { visual: '🔵', label: 'cerchio blu' },
  { visual: '🟡', label: 'cerchio giallo' },
  { visual: '🟢', label: 'cerchio verde' },
  { visual: '🟣', label: 'cerchio viola' }
] as const;

const SHAPES = [
  { visual: '○', label: 'cerchio', sides: 0 },
  { visual: '△', label: 'triangolo', sides: 3 },
  { visual: '□', label: 'quadrato', sides: 4 },
  { visual: '▭', label: 'rettangolo', sides: 4 }
] as const;

const ITALIAN_ALPHABET = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'] as const;
const VOWELS = [...'AEIOU'] as const;
const CONSONANTS = [...'BCDFGLMNPRSTV'] as const;

const INITIAL_WORDS = [
  { letter: 'M', visual: '🍎', label: 'mela' },
  { letter: 'S', visual: '☀️', label: 'sole' },
  { letter: 'C', visual: '🐶', label: 'cane' },
  { letter: 'P', visual: '🍞', label: 'pane' },
  { letter: 'L', visual: '🌙', label: 'luna' },
  { letter: 'F', visual: '🌼', label: 'fiore' },
  { letter: 'G', visual: '🐱', label: 'gatto' },
  { letter: 'T', visual: '🍰', label: 'torta' }
] as const;

const SYLLABLE_WORDS = [
  { word: 'casa', syllables: 2 },
  { word: 'mela', syllables: 2 },
  { word: 'sole', syllables: 2 },
  { word: 'gatto', syllables: 2 },
  { word: 'pane', syllables: 2 },
  { word: 'luna', syllables: 2 },
  { word: 'banana', syllables: 3 },
  { word: 'tavolo', syllables: 3 },
  { word: 'matita', syllables: 3 },
  { word: 'pecora', syllables: 3 },
  { word: 'carota', syllables: 3 },
  { word: 'telefono', syllables: 4 }
] as const;

const PUNCTUATION_CASES = [
  { text: 'Come ti chiami', mark: '?' },
  { text: 'Che bella sorpresa', mark: '!' },
  { text: 'Oggi andiamo a scuola', mark: '.' },
  { text: 'Dove hai messo il libro', mark: '?' },
  { text: 'Attento alla pozzanghera', mark: '!' },
  { text: 'La luna è alta nel cielo', mark: '.' }
] as const;

const MICRO_STORIES = [
  { text: 'Anna gioca nel parco.', character: 'Anna', place: 'parco' },
  { text: 'Luca legge un libro in biblioteca.', character: 'Luca', place: 'biblioteca' },
  { text: 'Marta mangia una mela in cucina.', character: 'Marta', place: 'cucina' },
  { text: 'Paolo corre nel giardino.', character: 'Paolo', place: 'giardino' },
  { text: 'Sara disegna in classe.', character: 'Sara', place: 'classe' },
  { text: 'Nico guarda i pesci al lago.', character: 'Nico', place: 'lago' }
] as const;

function definition(kind: ExerciseKind): ExerciseDefinition {
  const value = EXERCISES.find((exercise) => exercise.kind === kind);
  if (!value) throw new Error(`Unknown exercise family: ${kind}`);
  return value;
}

function pick<T>(values: readonly T[], random: RandomSource): T {
  const value = values[Math.floor(random() * values.length)] ?? values[0];
  if (value === undefined) throw new Error('Cannot pick from an empty collection');
  return value;
}

function integer(min: number, max: number, random: RandomSource): number {
  return min + Math.floor(random() * (max - min + 1));
}

export function shuffle<T>(values: readonly T[], random: RandomSource): T[] {
  const copy = [...values];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    const current = copy[index];
    copy[index] = copy[other]!;
    copy[other] = current!;
  }
  return copy;
}

function numericOptions(correct: number, min: number, max: number, random: RandomSource) {
  const candidates = Array.from({ length: max - min + 1 }, (_, index) => min + index).filter(
    (value) => value !== correct
  );
  return shuffle([correct, ...shuffle(candidates, random).slice(0, 2)], random).map((value, index) => ({
    value: index,
    visual: String(value),
    label: String(value)
  }));
}

function instructionChoice(
  family: ExerciseDefinition,
  id: string,
  instruction: string,
  retryInstruction: string,
  correctVisual: string,
  options: readonly { visual: string; label: string }[],
  random: RandomSource
): InstructionExerciseQuestion {
  const choices = shuffle(options, random).map((option, index) => ({ ...option, value: index }));
  const expectedAnswer = choices.findIndex((choice) => choice.visual === correctVisual);
  if (expectedAnswer < 0) throw new Error(`Correct choice missing for ${id}`);
  return {
    id,
    kind: 'instruction',
    familyId: family.familyId,
    skillId: family.skillId,
    instruction,
    retryInstruction,
    expectedAnswer,
    choices
  };
}

function sequenceChoice(
  family: ExerciseDefinition,
  id: string,
  instruction: string,
  retryInstruction: string,
  pattern: readonly string[],
  correctVisual: string,
  options: readonly { visual: string; label: string }[],
  random: RandomSource
): SequenceExerciseQuestion {
  const choices = shuffle(options, random).map((option, index) => ({ ...option, value: index }));
  const expectedAnswer = choices.findIndex((choice) => choice.visual === correctVisual);
  if (expectedAnswer < 0) throw new Error(`Correct choice missing for ${id}`);
  return {
    id,
    kind: 'sequence',
    familyId: family.familyId,
    skillId: family.skillId,
    instruction,
    retryInstruction,
    expectedAnswer,
    pattern,
    choices
  };
}

function bankQuestions(family: ExerciseDefinition, random: RandomSource): InstructionExerciseQuestion[] {
  const items = family.items ?? [];
  return shuffle(items, random).slice(0, 5).map((item) => {
    const correct = item.choices[item.correctIndex];
    if (!correct) throw new Error(`Invalid correctIndex for ${family.familyId}/${item.id}`);
    return instructionChoice(
      family,
      `${family.familyId}-${item.id}`,
      item.prompt,
      'Rileggi o ascolta la consegna e prova ancora.',
      correct.visual,
      item.choices,
      random
    );
  });
}

function quantityQuestions(random: RandomSource): QuantityExerciseQuestion[] {
  const family = definition('quantity');
  return shuffle([1, 2, 3, 4, 5], random).map((count, index) => {
    const symbol = pick(SYMBOLS, random);
    return {
      id: `quantity-${index}-${count}-${symbol.label}`,
      kind: 'quantity',
      familyId: family.familyId,
      skillId: family.skillId,
      instruction: 'Conta gli oggetti. Tocca il numero giusto.',
      retryInstruction: 'Prova ancora. Conta lentamente.',
      expectedAnswer: count,
      symbol: symbol.visual,
      count,
      choices: shuffle([1, 2, 3, 4, 5], random)
    };
  });
}

function compareQuestions(random: RandomSource): CompareExerciseQuestion[] {
  const family = definition('compare');
  return Array.from({ length: 5 }, (_, index) => {
    const counts = shuffle([1, 2, 3, 4, 5], random).slice(0, 2);
    const symbol = pick(SYMBOLS, random);
    const left = counts[0] ?? 1;
    const right = counts[1] ?? 2;
    return {
      id: `compare-${index}-${left}-${right}-${symbol.label}`,
      kind: 'compare',
      familyId: family.familyId,
      skillId: family.skillId,
      instruction: 'Dove ce ne sono di più?',
      retryInstruction: 'Guarda bene i due gruppi. Dove ce ne sono di più?',
      expectedAnswer: left > right ? 0 : 1,
      groups: [
        { symbol: symbol.visual, count: left, value: 0 },
        { symbol: symbol.visual, count: right, value: 1 }
      ]
    };
  });
}

function sequenceQuestions(random: RandomSource): SequenceExerciseQuestion[] {
  const family = definition('sequence');
  return Array.from({ length: 5 }, (_, index) => {
    const shuffled = shuffle(PATTERN_SYMBOLS, random);
    const a = shuffled[0]!;
    const b = shuffled[1]!;
    const distractor = shuffled[2]!;
    const abb = random() >= 0.5;
    const pattern = abb
      ? [a.visual, b.visual, b.visual, a.visual, b.visual, b.visual]
      : [a.visual, b.visual, a.visual, b.visual, a.visual, b.visual];
    return sequenceChoice(
      family,
      `sequence-${index}-${abb ? 'abb' : 'ab'}-${a.label}-${b.label}`,
      'Cosa viene dopo?',
      'Guarda il ritmo. Cosa viene dopo?',
      pattern,
      a.visual,
      [a, b, distractor],
      random
    );
  });
}

function instructionQuestions(random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition('instruction');
  return Array.from({ length: 5 }, (_, index) => {
    const options = shuffle(SYMBOLS, random).slice(0, 3);
    const target = pick(options, random);
    const article = ['mela', 'stella', 'coccinella', 'macchina'].includes(target.label) ? 'la' : 'il';
    return instructionChoice(
      family,
      `instruction-${index}-${target.label}`,
      `Tocca ${article} ${target.label}.`,
      'Ascolta ancora e prova di nuovo.',
      target.visual,
      options,
      random
    );
  });
}

function numberRelationQuestions(
  kind: 'number-next' | 'number-previous' | 'number-greater' | 'number-between',
  random: RandomSource
): ExerciseQuestion[] {
  const family = definition(kind);
  return Array.from({ length: 5 }, (_, index) => {
    if (kind === 'number-next') {
      const current = integer(1, 9, random);
      const correct = current + 1;
      return sequenceChoice(
        family,
        `${kind}-${index}-${current}`,
        'Quale numero viene dopo?',
        'Conta in avanti di uno.',
        [String(current)],
        String(correct),
        numericOptions(correct, 1, 10, random),
        random
      );
    }
    if (kind === 'number-previous') {
      const current = integer(2, 10, random);
      const correct = current - 1;
      return instructionChoice(
        family,
        `${kind}-${index}-${current}`,
        `Quale numero viene prima di ${current}?`,
        'Conta indietro di uno.',
        String(correct),
        numericOptions(correct, 1, 10, random),
        random
      );
    }
    if (kind === 'number-greater') {
      const numbers = shuffle(Array.from({ length: 10 }, (_, value) => value + 1), random).slice(0, 2);
      const left = numbers[0] ?? 1;
      const right = numbers[1] ?? 2;
      const correct = Math.max(left, right);
      return instructionChoice(
        family,
        `${kind}-${index}-${left}-${right}`,
        `Tocca il numero più grande tra ${left} e ${right}.`,
        'Confronta i due numeri.',
        String(correct),
        [left, right].map((value) => ({ visual: String(value), label: String(value) })),
        random
      );
    }
    const middle = integer(2, 9, random);
    return instructionChoice(
      family,
      `${kind}-${index}-${middle}`,
      `Quale numero sta tra ${middle - 1} e ${middle + 1}?`,
      'Conta in ordine.',
      String(middle),
      numericOptions(middle, 1, 10, random),
      random
    );
  });
}

function arithmeticQuestions(
  kind: 'visual-addition' | 'visual-subtraction',
  random: RandomSource
): SequenceExerciseQuestion[] {
  const family = definition(kind);
  return Array.from({ length: 5 }, (_, index) => {
    const symbol = pick(SYMBOLS.slice(0, 4), random);
    if (kind === 'visual-addition') {
      const left = integer(1, 4, random);
      const right = integer(1, 5 - left, random);
      const correct = left + right;
      return sequenceChoice(
        family,
        `${kind}-${index}-${left}-${right}-${symbol.label}`,
        'Quanti sono in tutto?',
        'Conta il primo gruppo, poi aggiungi il secondo.',
        [symbol.visual.repeat(left), '+', symbol.visual.repeat(right), '='],
        String(correct),
        numericOptions(correct, 1, 5, random),
        random
      );
    }
    const total = integer(2, 5, random);
    const removed = integer(1, total - 1, random);
    const correct = total - removed;
    return sequenceChoice(
      family,
      `${kind}-${index}-${total}-${removed}-${symbol.label}`,
      'Quanti ne restano?',
      'Parti dal primo gruppo e togli il secondo.',
      [symbol.visual.repeat(total), '−', symbol.visual.repeat(removed), '='],
      String(correct),
      numericOptions(correct, 1, 5, random),
      random
    );
  });
}

function shapeQuestions(kind: 'shape-name' | 'shape-property', random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition(kind);
  return Array.from({ length: 5 }, (_, index) => {
    if (kind === 'shape-name') {
      const target = pick(SHAPES, random);
      const distractors = shuffle(SHAPES.filter((shape) => shape.visual !== target.visual), random).slice(0, 2);
      return instructionChoice(
        family,
        `${kind}-${index}-${target.label}`,
        `Tocca il ${target.label}.`,
        'Guarda bene la forma.',
        target.visual,
        [target, ...distractors],
        random
      );
    }
    const sides = random() < 0.5 ? 3 : 4;
    const triangle = SHAPES[1]!;
    const square = SHAPES[2]!;
    const rectangle = SHAPES[3]!;
    const circle = SHAPES[0]!;
    const target = sides === 3 ? triangle : pick([square, rectangle], random);
    const distractors = sides === 3 ? [circle, square] : [circle, triangle];
    return instructionChoice(
      family,
      `${kind}-${index}-${sides}-${target.label}`,
      `Tocca una figura con ${sides === 3 ? 'tre' : 'quattro'} lati.`,
      'Conta i lati delle figure.',
      target.visual,
      [target, ...distractors],
      random
    );
  });
}

function alphabetNextQuestions(random: RandomSource): SequenceExerciseQuestion[] {
  const family = definition('alphabet-next');
  return Array.from({ length: 5 }, (_, index) => {
    const start = integer(0, ITALIAN_ALPHABET.length - 2, random);
    const current = ITALIAN_ALPHABET[start]!;
    const correct = ITALIAN_ALPHABET[start + 1]!;
    const distractors = shuffle(
      ITALIAN_ALPHABET.filter((letter) => letter !== correct && letter !== current),
      random
    ).slice(0, 2);
    return sequenceChoice(
      family,
      `alphabet-next-${index}-${current}`,
      'Quale lettera viene dopo?',
      'Ripeti l’alfabeto in ordine.',
      [current],
      correct,
      [correct, ...distractors].map((letter) => ({ visual: letter, label: `lettera ${letter}` })),
      random
    );
  });
}

function vowelQuestions(random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition('vowel-recognition');
  return Array.from({ length: 5 }, (_, index) => {
    const target = pick(VOWELS, random);
    const distractors = shuffle(CONSONANTS, random).slice(0, 2);
    return instructionChoice(
      family,
      `vowel-${index}-${target}`,
      'Tocca la vocale.',
      'Cerca una tra A, E, I, O, U.',
      target,
      [target, ...distractors].map((letter) => ({ visual: letter, label: `lettera ${letter}` })),
      random
    );
  });
}

function initialLetterQuestions(random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition('initial-letter');
  return Array.from({ length: 5 }, (_, index) => {
    const target = pick(INITIAL_WORDS, random);
    const distractors = shuffle(INITIAL_WORDS.filter((item) => item.letter !== target.letter), random).slice(0, 2);
    return instructionChoice(
      family,
      `initial-letter-${index}-${target.letter}-${target.label}`,
      `Quale parola inizia con ${target.letter}?`,
      `Ascolta il primo suono: ${target.letter}.`,
      target.visual,
      [target, ...distractors],
      random
    );
  });
}

function syllableQuestions(random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition('syllable-count');
  return Array.from({ length: 5 }, (_, index) => {
    const item = pick(SYLLABLE_WORDS, random);
    return instructionChoice(
      family,
      `syllables-${index}-${item.word}`,
      `Quante sillabe ha la parola ${item.word}?`,
      'Pronuncia lentamente la parola e dividila in parti.',
      String(item.syllables),
      numericOptions(item.syllables, 1, 4, random),
      random
    );
  });
}

function punctuationQuestions(random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition('punctuation-mark');
  return Array.from({ length: 5 }, (_, index) => {
    const item = pick(PUNCTUATION_CASES, random);
    const options = ['.', '?', '!'].map((mark) => ({
      visual: mark,
      label: mark === '.' ? 'punto' : mark === '?' ? 'punto interrogativo' : 'punto esclamativo'
    }));
    return instructionChoice(
      family,
      `punctuation-${index}-${item.mark}-${item.text}`,
      `Quale segno completa: ${item.text}`,
      'Pensa se la frase afferma, domanda o esclama.',
      item.mark,
      options,
      random
    );
  });
}

function twoStepQuestions(random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition('listen-two-step');
  return Array.from({ length: 5 }, (_, index) => {
    const optionsPool = shuffle(SYMBOLS, random).slice(0, 3);
    const first = optionsPool[0]!;
    const second = optionsPool[1]!;
    const third = optionsPool[2]!;
    const correctVisual = `${first.visual} → ${second.visual}`;
    return instructionChoice(
      family,
      `two-step-${index}-${first.label}-${second.label}`,
      `Prima tocca ${first.label}, poi ${second.label}. Quale sequenza è giusta?`,
      'Ascolta ancora: conta l’ordine dei due passi.',
      correctVisual,
      [
        { visual: correctVisual, label: `prima ${first.label}, poi ${second.label}` },
        { visual: `${second.visual} → ${first.visual}`, label: `prima ${second.label}, poi ${first.label}` },
        { visual: `${first.visual} → ${third.visual}`, label: `prima ${first.label}, poi ${third.label}` }
      ],
      random
    );
  });
}

function textElementQuestions(kind: 'text-character' | 'text-place', random: RandomSource): InstructionExerciseQuestion[] {
  const family = definition(kind);
  return Array.from({ length: 5 }, (_, index) => {
    const story = pick(MICRO_STORIES, random);
    const correct = kind === 'text-character' ? story.character : story.place;
    const pool = MICRO_STORIES.map((item) => (kind === 'text-character' ? item.character : item.place));
    const distractors = shuffle([...new Set(pool.filter((value) => value !== correct))], random).slice(0, 2);
    return instructionChoice(
      family,
      `${kind}-${index}-${correct}`,
      `${story.text} ${kind === 'text-character' ? 'Chi è il personaggio?' : 'Dove si svolge la frase?'}`,
      'Ascolta o leggi di nuovo la frase e cerca l’informazione richiesta.',
      correct,
      [correct, ...distractors].map((value) => ({ visual: value, label: value })),
      random
    );
  });
}

export function generateExerciseSession(kind: ExerciseKind, random: RandomSource = Math.random): ExerciseQuestion[] {
  const family = definition(kind);
  if (family.items?.length) return bankQuestions(family, random);

  switch (kind) {
    case 'compare':
      return compareQuestions(random);
    case 'sequence':
      return sequenceQuestions(random);
    case 'instruction':
      return instructionQuestions(random);
    case 'number-next':
    case 'number-previous':
    case 'number-greater':
    case 'number-between':
      return numberRelationQuestions(kind, random);
    case 'visual-addition':
    case 'visual-subtraction':
      return arithmeticQuestions(kind, random);
    case 'shape-name':
    case 'shape-property':
      return shapeQuestions(kind, random);
    case 'alphabet-next':
      return alphabetNextQuestions(random);
    case 'vowel-recognition':
      return vowelQuestions(random);
    case 'initial-letter':
      return initialLetterQuestions(random);
    case 'syllable-count':
      return syllableQuestions(random);
    case 'punctuation-mark':
      return punctuationQuestions(random);
    case 'listen-two-step':
      return twoStepQuestions(random);
    case 'text-character':
    case 'text-place':
      return textElementQuestions(kind, random);
    case 'quantity':
      return quantityQuestions(random);
    default:
      throw new Error(`Exercise family ${kind} has no generator or item bank`);
  }
}
